import { PersistentChatMessageData } from "~shared/types/persistent-chat-types";
import type { PlanData, CommentingData } from "src/stores/persistentChatStore";
import { normalizeTerminalPlanStatus } from "src/lib/normalizeTerminalPlanStatus";

type PlanMessageChoice = {
  messageId: string;
  isExecutionMessage: boolean;
};

/**
 * Chooses one terminal plan card message per plan id.
 * Prefer the non-execution message when both proposal and execution messages contain the same terminal plan.
 */
export function selectTerminalPlanCardMessageIds(messages: PersistentChatMessageData[]): Set<string> {
  const selectedByPlanId = new Map<string, PlanMessageChoice>();

  for (const message of messages) {
    const rawPlan = message.metadata?.plan as PlanData | undefined;
    if (!rawPlan?.id) continue;
    const plan = normalizeTerminalPlanStatus(rawPlan);
    if (plan.status !== "completed" && plan.status !== "cancelled") continue;

    const isExecutionMessage = Boolean((message.metadata as any)?.planExecution);
    const existing = selectedByPlanId.get(plan.id);

    if (!existing) {
      selectedByPlanId.set(plan.id, { messageId: message.id, isExecutionMessage });
      continue;
    }

    if (existing.isExecutionMessage && !isExecutionMessage) {
      selectedByPlanId.set(plan.id, { messageId: message.id, isExecutionMessage });
    }
  }

  return new Set(Array.from(selectedByPlanId.values()).map((choice) => choice.messageId));
}

/** Chooses one terminal commenting card message per commenting session id. */
export function selectTerminalCommentingCardMessageIds(messages: PersistentChatMessageData[]): Set<string> {
  const selectedByCommentingId = new Map<string, string>();

  for (const message of messages) {
    const rawCommenting = message.metadata?.commenting as CommentingData | undefined;
    if (!rawCommenting?.id) continue;
    if (rawCommenting.status === "in_progress") continue;
    if (!selectedByCommentingId.has(rawCommenting.id)) {
      selectedByCommentingId.set(rawCommenting.id, message.id);
    }
  }

  return new Set(selectedByCommentingId.values());
}
